import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SectionTrustBar from "../../components/feature/sections/SectionTrustBar";
import SectionAIBlock from "../../components/feature/sections/SectionAIBlock";
import SectionProcess from "../../components/feature/sections/SectionProcess";
import SectionWhyChooseUs from "../../components/feature/sections/SectionWhyChooseUs";
import SectionServiceAreas from "../../components/feature/sections/SectionServiceAreas";
import SectionTestimonials from "../../components/feature/sections/SectionTestimonials";
import SectionFAQ from "../../components/feature/sections/SectionFAQ";
import SectionFinalCTA from "../../components/feature/sections/SectionFinalCTA";

const FORM_URL = "https://readdy.ai/api/form/d6uqg8mdp0k8rarn7os0";

const services = [
  "Health Law",
  "Medicare Compliance",
  "Business Law",
  "Litigation",
  "Contract Disputes",
  "Real Estate Law",
  "HIPAA Compliance",
  "Managed Care Contracting",
  "Other",
];

const contactAIQuestions = [
  {
    q: "How do I schedule a consultation with this law firm?",
    a: "You can schedule a consultation by calling (215) 735-2384 during business hours (Mon–Fri, 9 AM–5:30 PM) or by submitting our online consultation request form. We respond to all requests within one business day and provide a confidential, no-obligation initial consultation.",
  },
  {
    q: "What should I expect during a consultation?",
    a: "During an initial consultation, we will review your legal situation, identify the relevant regulatory or legal issues, explain how our expertise applies to your matter, and outline a potential strategy. We aim to provide clarity and actionable guidance from the very first conversation.",
  },
  {
    q: "Is the initial consultation free and confidential?",
    a: "Yes. Our initial consultations are confidential and no-obligation. Information shared during a consultation is protected by attorney-client privilege. Submitting a contact form or calling our office does not create an attorney-client relationship until formally established in writing.",
  },
];

const contactFAQs = [
  {
    q: "How quickly will the firm respond to my inquiry?",
    a: "We respond to all consultation requests within one business day. For urgent matters, please call our office directly at (215) 735-2384 during business hours (Mon–Fri, 9 AM–5:30 PM) for immediate assistance.",
  },
  {
    q: "How much does a health care attorney cost?",
    a: "Legal fees vary depending on the type and complexity of your legal matter. We provide transparent fee structures and, after an initial consultation, will give you a clear understanding of the expected scope and cost of representation before any engagement begins.",
  },
  {
    q: "Do I need to come to your Philadelphia office in person?",
    a: "Not necessarily. We regularly advise clients throughout the United States by phone and video conference. For local Philadelphia-area clients, we are happy to meet in person at our Rittenhouse Square office.",
  },
  {
    q: "What information should I have ready for my consultation?",
    a: "For the most productive initial consultation, it helps to have a brief summary of your legal issue, any relevant documents (contracts, government notices, audit letters), and specific questions you need answered. However, you can also simply call or reach out and we will guide the conversation.",
  },
];

function ContactForm() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      setError("Please fill in your name and email address.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.service || !formData.message.trim()) {
      setError("Please select a practice area and describe your matter.");
      return;
    }
    if (formData.message.length > 500) {
      setError("Message must be 500 characters or fewer.");
      return;
    }
    setError("");
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      });
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("There was an issue submitting your request. Please call us directly.");
      }
    } catch {
      setError("Network error. Please try again or call us directly.");
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-14">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C9A961]/10 border border-[#C9A961]/30 mx-auto mb-5">
          <i className="ri-check-line text-[#C9A961] text-2xl" />
        </div>
        <h3 className="text-2xl font-playfair font-bold text-[#0A1628] mb-3">Request Received</h3>
        <p className="text-slate-500 font-inter text-sm leading-relaxed max-w-sm mx-auto">
          Thank you for reaching out. A member of our team will contact you within one business
          day to schedule your consultation.
        </p>
        <p className="mt-5 text-sm text-slate-400 font-inter">
          Need immediate assistance?{" "}
          <a href="tel:+12157352384" className="text-[#C9A961] cursor-pointer font-medium">
            (215) 735-2384
          </a>
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Step indicator */}
      <div className="flex items-center gap-3 mb-7">
        {[1, 2].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-inter font-semibold transition-all ${
                step >= s ? "bg-[#0A1628] text-[#C9A961]" : "bg-slate-100 text-slate-400"
              }`}
            >
              {step > s ? <i className="ri-check-line text-xs" /> : s}
            </div>
            <span
              className={`text-xs font-inter ${step >= s ? "text-[#0A1628]" : "text-slate-400"}`}
            >
              {s === 1 ? "Your Information" : "Your Matter"}
            </span>
            {s < 2 && <i className="ri-arrow-right-s-line text-slate-300 ml-1" />}
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-xs font-inter">
          {error}
        </div>
      )}

      {step === 1 ? (
        <form data-readdy-form onSubmit={handleNext} className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-inter font-semibold text-[#0A1628] mb-1.5">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm font-inter text-[#0A1628] focus:outline-none focus:border-[#C9A961] transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-inter font-semibold text-[#0A1628] mb-1.5">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm font-inter text-[#0A1628] focus:outline-none focus:border-[#C9A961] transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-inter font-semibold text-[#0A1628] mb-1.5">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(215) 555-0100"
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm font-inter text-[#0A1628] focus:outline-none focus:border-[#C9A961] transition-colors"
            />
          </div>
          <button
            type="submit"
            className="bg-[#0A1628] hover:bg-[#152238] text-white font-inter font-semibold py-3.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm mt-1"
          >
            Continue <i className="ri-arrow-right-line ml-1" />
          </button>
        </form>
      ) : (
        <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-inter font-semibold text-[#0A1628] mb-1.5">
              Practice Area <span className="text-red-500">*</span>
            </label>
            <select
              name="service"
              value={formData.service}
              onChange={handleChange}
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm font-inter text-[#0A1628] focus:outline-none focus:border-[#C9A961] transition-colors bg-white cursor-pointer"
              required
            >
              <option value="">Select practice area...</option>
              {services.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-inter font-semibold text-[#0A1628] mb-1.5">
              Describe Your Matter <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Please briefly describe your legal matter (500 characters max)..."
              rows={4}
              maxLength={500}
              className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm font-inter text-[#0A1628] focus:outline-none focus:border-[#C9A961] transition-colors resize-none"
              required
            />
            <div
              className={`text-right text-xs font-inter mt-1 ${
                charCount >= 480 ? "text-red-500" : "text-slate-400"
              }`}
            >
              {charCount}/500
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex-1 border border-slate-200 text-slate-600 font-inter font-medium py-3.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm hover:border-slate-400"
            >
              <i className="ri-arrow-left-line mr-1" /> Back
            </button>
            <button
              type="submit"
              className="flex-1 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-bold py-3.5 px-6 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              Submit Request <i className="ri-send-plane-line ml-1" />
            </button>
          </div>
          <p className="text-xs text-slate-400 font-inter text-center">
            Confidential. Submission does not create an attorney-client relationship.
          </p>
        </form>
      )}
    </>
  );
}

export default function ContactPage() {
  return (
    <div className="font-inter">
      <Navbar />
      <main>
        {/* ① HERO — with above-fold form */}
        <section className="relative bg-[#0A1628] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/a3c31af7ef93fde0c49df01b9fda6644.jpg"
              alt=""
              className="w-full h-full object-cover opacity-15"
            />
            <div className="absolute inset-0 bg-[#0A1628]/80" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 pt-36">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Left */}
              <div className="pt-4">
                <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-4">
                  Get in Touch
                </span>
                <h1 className="text-5xl lg:text-[60px] font-playfair font-bold text-white leading-[1.06] mb-5">
                  Schedule Your<br />
                  <span className="italic text-[#C9A961]">Free Consultation</span>
                </h1>
                <p className="text-slate-300 font-inter text-lg leading-relaxed mb-8 max-w-lg">
                  Tell us about your legal matter and our experienced health law team will respond
                  within one business day. Confidential. No obligation.
                </p>
                <div className="flex flex-col gap-4 mb-8">
                  {[
                    { icon: "ri-phone-line", label: "Phone", val: "(215) 735-2384", href: "tel:+12157352384" },
                    { icon: "ri-mail-line", label: "Email", val: "info@gosfield.com", href: "mailto:info@gosfield.com" },
                    { icon: "ri-map-pin-2-line", label: "Office", val: "2309 Delancey Pl., Philadelphia, PA 19103", href: null },
                    { icon: "ri-time-line", label: "Hours", val: "Mon–Fri, 9:00 AM – 5:30 PM ET", href: null },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3">
                      <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#C9A961]/15 border border-[#C9A961]/20 flex-shrink-0">
                        <i className={`${item.icon} text-[#C9A961] text-base`} />
                      </span>
                      <div>
                        <div className="text-[10px] text-slate-400 font-inter uppercase tracking-wider mb-0.5">
                          {item.label}
                        </div>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="text-sm font-inter font-medium text-slate-200 hover:text-[#C9A961] transition-colors cursor-pointer"
                          >
                            {item.val}
                          </a>
                        ) : (
                          <div className="text-sm font-inter font-medium text-slate-200">{item.val}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — form above fold */}
              <div className="bg-white rounded-2xl p-8">
                <div className="mb-6">
                  <h2 className="font-playfair font-bold text-[#0A1628] text-xl mb-1">
                    Request a Free Consultation
                  </h2>
                  <p className="text-slate-400 text-xs font-inter">
                    We respond to all inquiries within one business day.
                  </p>
                </div>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* ② TRUST BAR */}
        <SectionTrustBar />

        {/* ③ OFFICE + MAP */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-14">
              <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-3">
                Visit Us
              </span>
              <h2 className="text-4xl lg:text-5xl font-playfair font-bold text-[#0A1628] mb-4">
                Our Philadelphia Office
              </h2>
              <p className="text-slate-500 font-inter text-base max-w-xl mx-auto">
                Located in the prestigious Rittenhouse Square neighborhood of Philadelphia,
                easily accessible from throughout the metro area.
              </p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 rounded-xl overflow-hidden border border-slate-200" style={{ height: "420px" }}>
                <iframe
                  title="Contact — Gosfield &amp; Associates Philadelphia office"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3058.234580!2d-75.1762!3d39.9425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6c62b8e2b3a5f%3A0x0!2s2309+Delancey+Pl%2C+Philadelphia%2C+PA+19103!5e0!3m2!1sen!2sus!4v1640000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="lg:col-span-2 flex flex-col justify-center gap-5">
                {[
                  { icon: "ri-map-pin-2-line", label: "Address", val: "2309 Delancey Pl.\nPhiladelphia, PA 19103", href: null },
                  { icon: "ri-phone-line", label: "Main Phone", val: "(215) 735-2384", href: "tel:+12157352384" },
                  { icon: "ri-printer-line", label: "Fax", val: "(215) 735-4778", href: null },
                  { icon: "ri-mail-line", label: "Email", val: "info@gosfield.com", href: "mailto:info@gosfield.com" },
                  { icon: "ri-time-line", label: "Office Hours", val: "Mon–Fri: 9:00 AM – 5:30 PM", href: null },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4 p-4 rounded-xl bg-[#F8F7F4] border border-slate-100">
                    <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#0A1628] flex-shrink-0">
                      <i className={`${item.icon} text-[#C9A961] text-base`} />
                    </span>
                    <div>
                      <div className="text-[10px] text-slate-400 font-inter uppercase tracking-wider mb-0.5">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-sm font-inter font-semibold text-[#0A1628] hover:text-[#C9A961] transition-colors cursor-pointer whitespace-pre-line">{item.val}</a>
                      ) : (
                        <div className="text-sm font-inter font-semibold text-[#0A1628] whitespace-pre-line">{item.val}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ④ AI ANSWER BLOCK */}
        <SectionAIBlock
          questions={contactAIQuestions}
          title="About Consultations"
        />

        {/* ⑤ PROCESS */}
        <SectionProcess />

        {/* ⑥ WHY CHOOSE US */}
        <SectionWhyChooseUs />

        {/* ⑦ SERVICE AREAS */}
        <SectionServiceAreas />

        {/* ⑧ TESTIMONIALS */}
        <SectionTestimonials />

        {/* ⑨ FAQ */}
        <SectionFAQ
          faqs={contactFAQs}
          title="Consultation FAQ"
        />

        {/* ⑩ FINAL CTA */}
        <SectionFinalCTA
          headline="Ready to Speak With an Attorney Today?"
          subheadline="Take the first step toward resolving your legal matter. Our experienced health law team is ready to provide the guidance and advocacy you need."
          ctaLabel="Schedule Your Free Consultation"
        />
      </main>
      <Footer />
    </div>
  );
}
